import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";

export const Route = createFileRoute("/legal/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — BidNova Auctions" },
      { name: "description", content: "How BidNova Auctions collects, uses and protects your personal data under the UK GDPR and Data Protection Act 2018." },
      { property: "og:title", content: "Privacy Policy — BidNova Auctions" },
      { property: "og:description", content: "Our commitments under UK GDPR and the Data Protection Act 2018." },
      { property: "og:url", content: "https://bidnovaauctions.uk/legal/privacy" },
      { property: "og:type", content: "article" },
      ...socialImageMeta(),
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/legal/privacy" }],
    scripts: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Legal", path: "/legal/terms" }, { name: "Privacy Policy", path: "/legal/privacy" }])],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalPage eyebrow="Legal" title="Privacy Policy" updated="7 July 2026">
      <p>
        BidNova Auctions Ltd ("we", "us", "our") is the data controller of
        the personal data you provide when using bidnovaauctions.uk
        (the "Platform"). We process personal data in accordance with the UK
        GDPR, the Data Protection Act 2018, and the Privacy and Electronic
        Communications Regulations (PECR).
      </p>

      <h2>1. Data we collect</h2>
      <ul>
        <li><strong>Account data:</strong> name, email address, password (hashed), display name, avatar.</li>
        <li><strong>Verification data:</strong> for higher-value sellers, ID documents and address confirmation.</li>
        <li><strong>Transaction data:</strong> bids, purchases, sales, order history, tracking numbers, delivery confirmations, disputes.</li>
        <li><strong>Payment data:</strong> handled by Stripe Payments UK Ltd; we receive metadata (last 4 digits, brand, country) but never full card details.</li>
        <li><strong>Technical data:</strong> IP address, device/browser, timestamps, session cookies (see our <a href="/legal/cookies">Cookie Policy</a>).</li>
        <li><strong>Communications:</strong> messages you send to us or to other users through the Platform.</li>
      </ul>

      <h2>2. How we use your data and our legal bases</h2>
      <ul>
        <li><strong>Providing the Platform</strong> (Article 6(1)(b) — performance of a contract): account management, bidding, order fulfilment, payments, payouts, disputes.</li>
        <li><strong>Fraud prevention and platform safety</strong> (Article 6(1)(f) — legitimate interests): detecting shill bidding, counterfeit listings, fraudulent chargebacks.</li>
        <li><strong>Legal and tax compliance</strong> (Article 6(1)(c)): AML checks, HMRC digital-platform reporting, court/ICO orders.</li>
        <li><strong>Service communications</strong> (contract): auction results, payment receipts, dispute updates, security alerts.</li>
        <li><strong>Marketing</strong> (Article 6(1)(a) — consent): promotional emails; you can unsubscribe at any time.</li>
      </ul>

      <h2>3. Who we share data with</h2>
      <ul>
        <li>Sellers and buyers of a completed transaction (limited to information needed for delivery).</li>
        <li>Stripe Payments UK Ltd, our payment processor.</li>
        <li>Cloud infrastructure and email providers acting as our processors.</li>
        <li>HMRC and other authorities where legally required.</li>
        <li>Professional advisers, in confidence.</li>
      </ul>
      <p>We never sell your personal data.</p>

      <h2>4. International transfers</h2>
      <p>
        Where personal data is transferred outside the UK, we rely on the UK
        International Data Transfer Agreement (IDTA), the UK Addendum to the
        EU Standard Contractual Clauses, or an adequacy regulation.
      </p>

      <h2>5. Data retention</h2>
      <ul>
        <li>Account data: while your account is active, plus 6 years after closure for tax and legal records.</li>
        <li>Transaction and payment records: 6 years, in line with UK tax law.</li>
        <li>Marketing preferences: until you withdraw consent.</li>
        <li>Dispute evidence: 6 years from resolution.</li>
        <li>
          Advertising analytics: individual banner impression and click records are
          pseudonymised (we store a one-way hash, never your raw IP address) and are
          automatically deleted after 90 days. Only anonymous daily totals — impressions,
          clicks and unique visitor counts — are kept after that for advertiser reporting.
          Advertising analytics are only collected if you accept analytics cookies, and we
          can erase a campaign's raw analytics on request.
        </li>
      </ul>

      <h2>6. Your rights under UK GDPR</h2>
      <ul>
        <li>Access — request a copy of the personal data we hold about you.</li>
        <li>Rectification — ask us to correct inaccurate data.</li>
        <li>Erasure — ask us to delete your data (subject to legal retention obligations).</li>
        <li>Restriction and objection — limit or object to certain processing.</li>
        <li>Portability — receive your data in a structured, machine-readable format.</li>
        <li>Withdraw consent at any time (does not affect prior lawful processing).</li>
        <li>Complain to the UK Information Commissioner's Office (ICO) — <a href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>.</li>
      </ul>
      <p>
        To exercise a right, email <a href="mailto:privacy@bidnovaauctions.uk">privacy@bidnovaauctions.uk</a>.
        We respond within one month.
      </p>

      <h2>7. Security</h2>
      <p>
        We use TLS for all traffic, encrypt passwords with modern hashing,
        enforce access controls, and monitor for suspicious activity. No system
        is perfectly secure — if we become aware of a personal-data breach that
        is likely to result in a risk to your rights, we will notify the ICO
        within 72 hours and you where required.
      </p>

      <h2>8. Children</h2>
      <p>The Platform is not intended for anyone under 18 and we do not knowingly collect data from children.</p>

      <h2>9. Changes</h2>
      <p>We will notify you of material changes to this Policy by email or on-Platform notice.</p>

      <h2>10. Contact</h2>
      <p>
        Data controller: BidNova Auctions Ltd, registered in England &amp;
        Wales. Contact: <a href="mailto:privacy@bidnovaauctions.uk">privacy@bidnovaauctions.uk</a>.
      </p>
    </LegalPage>
  );
}
